import { callBaseAttributes, createLogger } from '@/services/logger/logger';
import { newRelicService } from '@/services/newrelic';

const qualityLogger = createLogger('CallQualityMonitor', ['calls', 'audio']);
const micSilenceLogger = createLogger('MicSilence', ['calls', 'audio']);
const noiseSuppressionLogger = createLogger('AudioDevices', ['calls', 'audio']);

/** Log a WebRTC metrics sample as a structured New Relic log + page action for session tracing */
export const logCallQualitySample = (attrs: {
  callId: string | undefined;
  quality: string;
  rtt: number | null;
  jitter: number | null;
  packetLoss: number | null;
  bitrate: number | null;
}): void => {
  const flat: Record<string, unknown> = {
    callId: attrs.callId,
    ...callBaseAttributes(attrs.callId),
    quality: attrs.quality,
    qosRtt: attrs.rtt,
    qosJitter: attrs.jitter,
    qosPacketLoss: attrs.packetLoss,
    qosBitrate: attrs.bitrate,
  };

  qualityLogger.info('Call quality sample', flat);

  // Page action creates a queryable event in New Relic session traces
  newRelicService.addPageAction('call:quality:sample', flat);
};

/** Log a quality transition (degradation or improvement) */
export const logCallQualityTransition = (attrs: {
  callId: string | undefined;
  previousQuality: string;
  newQuality: string;
  reason: string | null;
  rtt: number | null;
  jitter: number | null;
  packetLoss: number | null;
  callDurationSeconds: number;
}): void => {
  const flat: Record<string, unknown> = {
    callId: attrs.callId,
    ...callBaseAttributes(attrs.callId),
    previousQuality: attrs.previousQuality,
    newQuality: attrs.newQuality,
    reason: attrs.reason,
    qosRtt: attrs.rtt,
    qosJitter: attrs.jitter,
    qosPacketLoss: attrs.packetLoss,
    callDurationSeconds: attrs.callDurationSeconds,
  };

  qualityLogger.warn('Call quality transition', flat);

  newRelicService.addPageAction('call:quality:transition', flat);
};

/** Log an end-of-call quality summary with aggregated metrics */
export const logCallQualitySummary = (attrs: {
  callId: string | undefined;
  callDurationSeconds: number;
  predominantQuality: string;
  qualityBreakdown: Record<string, number>;
  peakRtt: number | null;
  peakJitter: number | null;
  peakPacketLoss: number | null;
  avgRtt: number | null;
  avgJitter: number | null;
  avgPacketLoss: number | null;
  transitionCount: number;
}): void => {
  const flat: Record<string, unknown> = {
    callId: attrs.callId,
    ...callBaseAttributes(attrs.callId),
    callDurationSeconds: attrs.callDurationSeconds,
    predominantQuality: attrs.predominantQuality,
    qosBreakdownGood: attrs.qualityBreakdown.good,
    qosBreakdownFair: attrs.qualityBreakdown.fair,
    qosBreakdownPoor: attrs.qualityBreakdown.poor,
    qosBreakdownCritical: attrs.qualityBreakdown.critical,
    qosPeakRtt: attrs.peakRtt,
    qosPeakJitter: attrs.peakJitter,
    qosPeakPacketLoss: attrs.peakPacketLoss,
    qosAvgRtt: attrs.avgRtt,
    qosAvgJitter: attrs.avgJitter,
    qosAvgPacketLoss: attrs.avgPacketLoss,
    transitionCount: attrs.transitionCount,
  };

  qualityLogger.info('Call quality summary', flat);

  newRelicService.addPageAction('call:quality:summary', flat);
};

/** Log mic silence detection */
export const logMicSilenceDetected = (attrs: {
  callId: string | undefined;
}): void => {
  const flat: Record<string, unknown> = {
    callId: attrs.callId,
    ...callBaseAttributes(attrs.callId),
  };

  micSilenceLogger.warn('Mic silence detected', flat);

  newRelicService.addPageAction('call:mic:silence:detected', flat);
};

/** Log mic silence resolved */
export const logMicSilenceResolved = (attrs: {
  callId: string | undefined;
}): void => {
  const flat: Record<string, unknown> = {
    callId: attrs.callId,
    ...callBaseAttributes(attrs.callId),
  };

  micSilenceLogger.info('Mic silence resolved', flat);

  newRelicService.addPageAction('call:mic:silence:resolved', flat);
};

/**
 * Log a noise suppression toggle.
 *
 * `requested` is what the user asked for; `applied` is what
 * `MediaStreamTrack.getSettings().noiseSuppression` reported after the new
 * track was acquired. When they diverge, the browser/device did not honor
 * the constraint (e.g., virtual devices, older drivers) — that divergence
 * is the signal we want to catch in production.
 */
export const logNoiseSuppressionToggled = (attrs: {
  callId: string | undefined;
  requested: boolean;
  applied: boolean | null;
  applyScope: 'in-call' | 'preference-only' | 'in-call-failed';
  microphoneLabel?: string;
  error?: string;
}): void => {
  const flat: Record<string, unknown> = {
    callId: attrs.callId,
    ...callBaseAttributes(attrs.callId),
    nsRequested: attrs.requested,
    nsApplied: attrs.applied,
    nsHonored: attrs.applied === null ? null : attrs.applied === attrs.requested,
    nsApplyScope: attrs.applyScope,
    nsMicrophoneLabel: attrs.microphoneLabel,
    nsError: attrs.error,
  };

  noiseSuppressionLogger.info('Noise suppression toggled', flat);

  newRelicService.addPageAction('call:noise-suppression:toggled', flat);
};
